import { PkBottomPanel, PkButton, PkIcon } from '@uefadigital/panenka-react';
import useThrottledCallback from 'beautiful-react-hooks/useThrottledCallback';
import useWindowScroll from 'beautiful-react-hooks/useWindowScroll';
import clsx from 'clsx';
import { memo, useEffect, useRef, useState } from 'react';

import ErrorBoundary from '@shared/ErrorBoundary';
import { useLocalStorage } from '@shared/hooks/useLocalStorage';
import { useTranslateFn } from '@shared/Translate';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { competitionCode, competitionUrl } from '@utilities/WindowContext';

import * as classes from './styles.module.scss';

const simulatorUrl = `/${competitionUrl}/simulator/` as const;
const localStorageKey = `show-${competitionCode}-simulator-promotion` as const;

const trackEvent = (name: string, interaction: string) => {
  GoogleTagManagerHelper.ga4Event(name, { section_reference: 'simulator', interaction });
};

const PromotionPanel = () => {
  const t = useTranslateFn();
  const [showPanel, setShowPanel] = useLocalStorage(localStorageKey, true);
  const [showButton, setShowButton] = useState(true);
  const panelRef = useRef<HTMLPkBottomPanelElement>(null);
  const onScroll = useWindowScroll();

  const lastScrollTop = useRef(0);
  const scrollCallback = useThrottledCallback(
    () => {
      const scrollDown = window.scrollY > lastScrollTop.current;
      lastScrollTop.current = window.scrollY;
      if (scrollDown) {
        setShowButton(false);
      } else {
        setShowButton(true);
      }
    },
    [],
    100,
  );

  onScroll(() => {
    if (showPanel) return;
    scrollCallback();
  });

  const buttonClicked = useRef(false);

  useEffect(() => {
    showPanel
      ? trackEvent('panel_dialog_loaded', 'Dialog - Loaded')
      : trackEvent('simulator_button_loaded', 'Load simulator button');
  }, [showPanel]);

  const handleDialogClose = () => {
    if (buttonClicked.current) {
      trackEvent('panel_dialog_skip', 'Dialog - Skip');
    }
    buttonClicked.current = false;
    setShowPanel(false);
    setShowButton(true);
  };

  const handleDialogMainClick = () => {
    trackEvent('panel_dialog_main_action', 'Dialog - Main action');
    buttonClicked.current = true;
    panelRef.current.close();
  };

  const handleDialogSecondaryClick = () => {
    trackEvent('panel_dialog_secondary_action', 'Dialog - Secondary action');
    buttonClicked.current = true;
    panelRef.current.close();
  };

  const handleBtnClick = () => {
    trackEvent('simulator_button_click', 'Click simulator button');
  };

  return (
    <ErrorBoundary>
      <PkBottomPanel
        ref={panelRef}
        className={classes.panel}
        focusOnOpen
        opened={showPanel}
        onPkClose={handleDialogClose}
      >
        <div className='pk-font-size--m pk-font--base-semibold' slot='title'>
          {t('see_how_unfold')}
        </div>
        <p>{t('enter-scorelines-simulator')}</p>
        <div className='pk-d--flex pk-gap--m' slot='actions'>
          <PkButton
            linkHref={simulatorUrl}
            className={clsx('pk-w--100', classes.btn)}
            width='auto'
            onPkClick={handleDialogMainClick}
          >
            {t('try_now')}
          </PkButton>
          <PkButton
            aria-label={t('not_now')}
            className={clsx('pk-w--100', classes.btn)}
            appearance='outlined'
            width='auto'
            onPkClick={handleDialogSecondaryClick}
          >
            {t('not_now')}
          </PkButton>
        </div>
      </PkBottomPanel>
      {!showPanel && (
        <PkButton
          width='auto'
          className={clsx('pk-left--50 pk-translate-middle--x', classes.floating, showButton && classes.show)}
          onPkClick={handleBtnClick}
          linkHref={simulatorUrl}
        >
          <PkIcon name='features-simulator-tool-standings-alt' size='24' className='pk-mr--xs' role='img' />
          <span className={classes.text}>{t('simulate_results')}</span>
        </PkButton>
      )}
    </ErrorBoundary>
  );
};

export default memo(PromotionPanel);
